/**
 * MobilePlanningView
 *
 * Main mobile wrapper for the Planning module.
 * Renders MobilePlanningTopMenu at the top, a horizontally scrollable
 * scheduler area in the middle, and a bottom bar with groupe filter chips.
 */
import React from "react";
import { Box, Chip } from "@mui/material";
import { Menu } from "primereact/menu";
import MobilePlanningTopMenu from "./MobilePlanningTopMenu";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const ORANGE = "#FF9015";
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * @param {Object} props
 * @param {string}          props.title            - Title text
 * @param {React.ReactNode} props.teamSelector     - TeamSelector component
 * @param {React.ReactNode} props.datePicker       - TransparentDatePicker component
 * @param {string}          props.selectedView     - "WEEKLY" | "MONTHLY"
 * @param {Function}        props.onViewChange     - (view: string) => void
 * @param {Object}          props.menuRef          - useRef for PrimeReact Menu
 * @param {Array}           props.menuItems        - PrimeReact Menu model items
 * @param {Array}           props.selectedShifts   - Currently selected shift IDs
 * @param {Function}        props.onDelete         - Delete handler
 * @param {Function}        props.onPublish        - Publish handler
 * @param {boolean}         props.isAdmin          - Whether user has manager/admin access
 * @param {boolean}         props.showPublish      - Whether to show publish button
 * @param {Array}           props.groupes          - List of groupes [{id, name, ...}, ...]
 * @param {number|null}     props.selectedGroupe   - Currently selected groupe ID (0 = all)
 * @param {Function}        props.onGroupeChange   - (groupeId: number) => void
 * @param {React.ReactNode} props.children         - Scheduler content
 * @param {React.ReactNode} props.samplesRow       - SamplesRow component (optional)
 * @param {boolean}         props.showSamples      - Whether samples row is visible
 */
export default function MobilePlanningView({
  title,
  teamSelector,
  datePicker,
  selectedView,
  onViewChange,
  menuRef,
  menuItems,
  selectedShifts = [],
  onDelete,
  onPublish,
  isAdmin,
  showPublish,
  groupes = [],
  selectedGroupe,
  onGroupeChange,
  children,
  samplesRow,
  showSamples,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        width: "100%",
        overflow: "hidden",
        bgcolor: "var(--surface-ground)",
      }}
    >
      {/* Sticky top menu */}
      <MobilePlanningTopMenu
        title={title}
        teamSelector={teamSelector}
        datePicker={datePicker}
        selectedView={selectedView}
        onViewChange={onViewChange}
        menuRef={menuRef}
        menuItems={menuItems}
        selectedShifts={selectedShifts}
        onDelete={onDelete}
        onPublish={onPublish}
        isAdmin={isAdmin}
        showPublish={showPublish}
      />

      {/* PrimeReact popup menu — rendered once, toggled via menuRef */}
      <Menu
        model={menuItems}
        popup
        ref={menuRef}
        id="popup_menu_right"
        popupAlignment="right"
        style={{ zIndex: 1500 }}
      />

      {/* Samples row (optional) */}
      {showSamples && samplesRow && (
        <Box
          sx={{
            width: "100%",
            pt: 1,
            px: 2,
            pb: 0,
            overflow: "hidden",
            transition: "height 0.2s ease",
          }}
        >
          {samplesRow}
        </Box>
      )}

      {/* Groupe filter chips — horizontal scroll */}
      {groupes.length > 0 && (
        <Box
          sx={{
            width: "100%",
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            flexShrink: 0,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* "All" chip */}
          <Chip
            label={<Langs str="All" />}
            size="small"
            variant={selectedGroupe === 0 || selectedGroupe == null ? "filled" : "outlined"}
            onClick={() => onGroupeChange && onGroupeChange(0)}
            sx={{
              flexShrink: 0,
              fontWeight: 600,
              fontSize: "0.78rem",
              height: 32,
              borderRadius: "10px",
              transition: TRANSITION_SMOOTH,
              ...(selectedGroupe === 0 || selectedGroupe == null
                ? {
                    bgcolor: ORANGE,
                    color: "#fff",
                    "&:hover": { bgcolor: ORANGE },
                    boxShadow: "0 2px 6px rgba(255,144,21,0.25)",
                  }
                : {
                    borderColor: "rgba(0,0,0,0.12)",
                    "&:hover": {
                      backgroundColor: "rgba(255,144,21,0.06)",
                    },
                  }),
              "&:active": {
                transform: "scale(0.96)",
              },
            }}
          />

          {groupes.map((groupe) => (
            <Chip
              key={groupe.id}
              label={groupe.name || groupe.label || `Groupe ${groupe.id}`}
              size="small"
              variant={selectedGroupe === groupe.id ? "filled" : "outlined"}
              onClick={() => onGroupeChange && onGroupeChange(groupe.id)}
              sx={{
                flexShrink: 0,
                fontWeight: 600,
                fontSize: "0.78rem",
                height: 32,
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                ...(selectedGroupe === groupe.id
                  ? {
                      bgcolor: ORANGE,
                      color: "#fff",
                      "&:hover": { bgcolor: ORANGE },
                      boxShadow: "0 2px 6px rgba(255,144,21,0.25)",
                    }
                  : {
                      borderColor: "rgba(0,0,0,0.12)",
                      "&:hover": {
                        backgroundColor: "rgba(255,144,21,0.06)",
                      },
                    }),
                "&:active": {
                  transform: "scale(0.96)",
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Scheduler content — single scroll container for both axes */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { width: 4, height: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.10)",
            borderRadius: 2,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
